/* ------------------------------------------------------------------
   BHC Kinetics — scroll-linked motion.

   One rAF loop drives everything. Element geometry is cached and only
   re-measured on resize or when the observer says an element entered
   the viewport, so scrolling never reads layout.

   Effects:
     parallax  — media drifts against the page as it crosses the viewport
     weight    — fast scrolling adds a little lag, so images feel heavy
     mask      — photos uncover from the bottom edge on entry
     rules     — hairlines draw left-to-right on entry
     edge      — dark sections gain a soft leading edge while arriving
     drift     — the marquee is nudged along by scroll position
     rows      — the lineup's index numbers light as they cross centre
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.__bhcKinetics) return;
  window.__bhcKinetics = true;

  var $$ = function (s, c) {
    return Array.prototype.slice.call((c || document).querySelectorAll(s));
  };

  var vh = window.innerHeight;
  var lastY = window.pageYOffset || 0;
  var velocity = 0;      // smoothed px/frame
  var running = false;

  /* ---------- registry ------------------------------------------- */
  // Each entry: { el, depth, top, height, kind }
  var parallax = [];
  var drifters = [];
  var rows = [];
  var edges = [];
  var pendingEntrance = [];   // masks/rules not yet triggered

  function measure(entry) {
    var r = entry.el.getBoundingClientRect();
    entry.top = r.top + (window.pageYOffset || 0);
    entry.height = r.height;
  }

  function register(el, depth, list) {
    var entry = { el: el, depth: depth, top: 0, height: 0 };
    measure(entry);
    list.push(entry);
    return entry;
  }

  /* ---------- collect ------------------------------------------- */
  function collect() {
    parallax.length = 0;
    drifters.length = 0;
    rows.length = 0;
    edges.length = 0;

    // Lifestyle band photos: the deeper the card sits in the row, the
    // slightly slower it travels, which separates the three planes.
    $$('.bhc-band__media').forEach(function (media, i) {
      var img = media.querySelector('.bhc-band__img');
      if (img) register(img, 0.055 + (i % 3) * 0.012, parallax);
    });

    var lineupImg = document.querySelector('.bhc-lineup__img');
    if (lineupImg) register(lineupImg, 0.05, parallax);

    $$('[data-bhc-drift]').forEach(function (el) {
      register(el, parseFloat(el.getAttribute('data-bhc-drift')) || 0.08, drifters);
    });

    $$('.bhc-lineup__row').forEach(function (el) {
      register(el, 0, rows);
    });

    $$('.bhc-band--dark, .bhc-lineup').forEach(function (el) {
      register(el, 0, edges);
    });
  }

  /* ---------- one-shot entrance effects -------------------------- */
  function tagEntrances() {
    // Photos uncover rather than fade.
    $$('.bhc-band__media, .bhc-lineup__media').forEach(function (el) {
      el.setAttribute('data-bhc-mask', '');
    });
    // The marquee is nudged along by scroll rather than looping on a
    // clock of its own. Selector list covers whichever inner element the
    // marquee section happens to use.
    $$('.bhc-marquee__track, .bhc-marquee__inner, .bhc-marquee__row').forEach(function (el) {
      if (!el.hasAttribute('data-bhc-drift')) el.setAttribute('data-bhc-drift', '0.1');
    });
  }

  function trigger(el) {
    el.classList.add(
      el.hasAttribute('data-bhc-mask') ? 'bhc-unmasked' : 'bhc-drawn'
    );
  }

  function watchEntrances() {
    var targets = $$('[data-bhc-mask], [data-bhc-rule]');
    pendingEntrance = targets.map(function (el) {
      return { el: el, top: 0, bottom: 0 };
    });
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach(trigger);
      pendingEntrance.length = 0;
      return;
    }

    measureEntrances();

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          trigger(e.target);
          io.unobserve(e.target);
        });
      },
      { threshold: 0, rootMargin: '0px 0px -6% 0px' }
    );
    targets.forEach(function (el) { io.observe(el); });
  }

  /* A fast scroll can outrun the observer, and an element taller than the
     viewport can sit across it without ever crossing a threshold, so the loop
     sweeps anything already on screen.

     This used to call getBoundingClientRect() on every pending element on
     every frame, which forces a synchronous layout mid-scroll — the single
     biggest cause of scroll jank in this file. Geometry is now measured once
     (and re-measured on resize) and compared against the scroll position
     instead, so the loop performs no layout reads at all. */
  function measureEntrances() {
    for (var i = 0; i < pendingEntrance.length; i++) {
      var el = pendingEntrance[i].el;
      var r = el.getBoundingClientRect();
      pendingEntrance[i].top = r.top + (window.pageYOffset || 0);
      pendingEntrance[i].bottom = pendingEntrance[i].top + r.height;
    }
  }

  function sweepEntrances(y) {
    if (!pendingEntrance.length) return;
    for (var i = pendingEntrance.length - 1; i >= 0; i--) {
      var entry = pendingEntrance[i];
      if (entry.el.classList.contains('bhc-unmasked') || entry.el.classList.contains('bhc-drawn')) {
        pendingEntrance.splice(i, 1);
        continue;
      }
      if (entry.top - y < vh * 0.94 && entry.bottom - y > 0) {
        trigger(entry.el);
        pendingEntrance.splice(i, 1);
      }
    }
  }

  /* ---------- the loop ------------------------------------------- */
  function frame() {
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    var raw = y - lastY;
    lastY = y;

    // Smooth the velocity so the lag eases in and out instead of
    // snapping on the first fast frame.
    velocity += (raw - velocity) * 0.18;
    var lag = Math.max(-26, Math.min(26, velocity * 0.55));

    var mid = y + vh / 2;
    var i, e, centre, progress, offset;

    // Parallax + weight
    for (i = 0; i < parallax.length; i++) {
      e = parallax[i];
      centre = e.top + e.height / 2;
      if (Math.abs(centre - mid) > vh * 1.4) continue;
      progress = (centre - mid) / vh;                 // -1 … 1 across view
      offset = -progress * e.height * e.depth + lag * (e.depth * 9);
      // Writing an identical value still costs a style recalculation, and at
      // rest the same number is produced frame after frame.
      if (e.last === undefined || Math.abs(offset - e.last) > 0.05) {
        e.last = offset;
        e.el.style.setProperty('--bhc-par', offset.toFixed(2) + 'px');
      }
    }

    // Marquee drift
    for (i = 0; i < drifters.length; i++) {
      e = drifters[i];
      centre = e.top + e.height / 2;
      if (Math.abs(centre - mid) > vh * 1.6) continue;
      var drift = -((centre - mid) / vh) * 120 * e.depth;
      if (e.last === undefined || Math.abs(drift - e.last) > 0.05) {
        e.last = drift;
        e.el.style.setProperty('--bhc-drift', drift.toFixed(2) + 'px');
      }
    }

    // Dark-section leading edge, strongest as the section arrives
    for (i = 0; i < edges.length; i++) {
      e = edges[i];
      var rel = (e.top - y) / vh;                     // 1 = one screen below
      var strength = rel > 0 && rel < 1 ? rel : 0;
      if (e.last === undefined || Math.abs(strength - e.last) > 0.004) {
        e.last = strength;
        e.el.style.setProperty('--bhc-edge', strength.toFixed(3));
      }
    }

    // Index rows light as they cross the middle band of the viewport
    for (i = 0; i < rows.length; i++) {
      e = rows[i];
      centre = e.top + e.height / 2 - y;
      e.el.classList.toggle(
        'bhc-row-lit',
        centre > vh * 0.28 && centre < vh * 0.72
      );
    }

    sweepEntrances(y);

    running = false;
  }

  function request() {
    if (running) return;
    running = true;
    window.requestAnimationFrame(frame);
  }

  function remeasure() {
    vh = window.innerHeight;
    parallax.forEach(measure);
    drifters.forEach(measure);
    rows.forEach(measure);
    edges.forEach(measure);
    measureEntrances();
    request();
  }

  function boot() {
    tagEntrances();
    collect();
    watchEntrances();
    request();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', remeasure, { passive: true });
  window.addEventListener('load', remeasure);
  document.addEventListener('shopify:section:load', function () {
    boot();
    remeasure();
  });

  // Late-loading images change layout; re-measure once they settle.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(remeasure);
  }
  setTimeout(remeasure, 1200);
})();
